import { useState, useEffect, useContext } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Collapse,
  Button,
  Link
} from '@mui/material';
import { ExpandMore, ExpandLess, Refresh, Download } from '@mui/icons-material';
import { DownloadNotificationService, DownloadNotification } from '../services/downloadNotificationService';
import { HttpServiceContext } from '../state/HttpProvider';

export const DownloadNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<(DownloadNotification & { id: string; expanded?: boolean; regenerating?: boolean })[]>([]);
  const { httpService } = useContext(HttpServiceContext);

  useEffect(() => {
    const unsubscribe = DownloadNotificationService.subscribe((notification) => {
      const id = Date.now().toString();
      const newNotification = { ...notification, id };
      
      setNotifications(prev => [...prev, newNotification]);

      // Auto-remove notification after duration (if duration > 0)
      if (notification.duration && notification.duration > 0) {
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== id));
        }, notification.duration);
      }
    });

    return unsubscribe;
  }, []);

  const handleClose = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const toggleExpanded = (id: string) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, expanded: !n.expanded } : n
    ));
  };

  const handleRegenerateLink = async (id: string, blobPath: string) => {
    try {
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, regenerating: true } : n
      ));

      const result = await httpService.regenerateExportLink(blobPath, new AbortController().signal);
      
      // Update notification with new link
      setNotifications(prev => prev.map(n => {
        if (n.id === id) {
          const expiryDate = new Date(result.expiresAt);
          const minutesUntilExpiry = Math.round((expiryDate.getTime() - Date.now()) / 60000);
          
          return {
            ...n,
            downloadUrl: result.downloadUrl,
            expiresAt: result.expiresAt,
            message: `${result.fileName} is ready for download. Link expires in ${minutesUntilExpiry} minutes.`,
            regenerating: false
          };
        }
        return n;
      }));

      DownloadNotificationService.success('Link Regenerated', 'Download link has been refreshed');
    } catch (error) {
      console.error('Error regenerating link:', error);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, regenerating: false } : n
      ));
      DownloadNotificationService.error('Regeneration Failed', error instanceof Error ? error.message : 'Failed to regenerate download link');
    }
  };

  return (
    <>
      {children}
      {notifications.map((notification, index) => (
        <Box
          key={notification.id}
          sx={{ 
            position: 'fixed',
            top: 16 + (index * 100),
            right: 16,
            maxWidth: '420px',
            width: 'calc(100% - 32px)',
            zIndex: 9999,
            animation: 'slideInRight 0.3s ease-out',
            '@keyframes slideInRight': {
              from: { transform: 'translateX(100%)', opacity: 0 },
              to: { transform: 'translateX(0)', opacity: 1 }
            },
            '@media (max-width: 600px)': {
              left: 16,
              right: 16,
              top: 16 + (index * 80),
              maxWidth: 'none'
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Success indicator bar */}
            {notification.type === 'success' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #ff3b3b 0%, #ff6b6b 100%)',
                }}
              />
            )}
            
            <Box sx={{ p: 2.5 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {notification.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.875rem'
                    }}
                  >
                    {notification.message}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleClose(notification.id)}
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': { 
                      color: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <Box component="span" sx={{ fontSize: '1.2rem' }}>×</Box>
                </IconButton>
              </Box>

              {/* File name */}
              {notification.fileName && (
                <Box 
                  sx={{ 
                    mb: 1.5,
                    p: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace'
                    }}
                  >
                    {notification.fileName}
                  </Typography>
                </Box>
              )}

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                {notification.downloadUrl && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Download fontSize="small" />}
                    component="a"
                    href={notification.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      flex: 1,
                      background: 'linear-gradient(90deg, #ff3b3b 0%, #ff6b6b 100%)',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      py: 1,
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(255, 59, 59, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #ff2b2b 0%, #ff5b5b 100%)',
                        boxShadow: '0 6px 16px rgba(255, 59, 59, 0.4)',
                      }
                    }}
                  >
                    Download File
                  </Button>
                )}

                {notification.showRegenerateButton && notification.blobPath && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={notification.regenerating ? null : <Refresh fontSize="small" />}
                    onClick={() => handleRegenerateLink(notification.id, notification.blobPath!)}
                    disabled={notification.regenerating}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      py: 1,
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.4)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    {notification.regenerating ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </>
  );
};